import {useEffect, useState, useRef} from 'react';
import {Monorail} from '@shopify/monorail';

import {
  ShoppingBagEvent,
  getShoppingBagEventPayload,
  toUnderscoreCase,
  ProductType,
} from './shoppingBagAnalytics';

interface TrekkieConfig {
  Trekkie: {
    appName: string;
  };
}

declare global {
  function handleTrekkieLoad(): void;
  interface Window {
    analytics: any;
    trekkie: any;
    bugsnagClient: any;
    hj: any;
    _hjSettings: {hjid: number; hjsv: number};

    /* last commit SHA set via sewing-kit.config:DefinePlugin */
    VERSION: string;
    utag: any;
  }
}

const getMonorail = (() => {
  let monorail: Monorail;

  return () => {
    if (!monorail) {
      monorail =
        process.env.NODE_ENV === 'production'
          ? Monorail.createHttpProducer({production: true})
          : Monorail.createLogProducer({debugMode: true});
    }

    return monorail;
  };
})();

// see here for monorail schema: https://github.com/Shopify/monorail/tree/master/schemas/linkpop
export enum MonorailEvent {
  AdminPageView = 'trekkie_linkpop_admin_page_view/1.0',
  PageView = 'trekkie_linkpop_page_view/2.0',
  ClickLandingPageCreatePage = 'linkpop_create_your_page_click/1.0',
  ClickLink = 'linkpop_link_click/3.0',
  ViewHome = 'trekkie_linkpop_landing_page_view/2.0',
  TrackShoppingBag = 'linkpop_shopping_bag_interaction/2.0',
  AmazonAffiliatePublicCardClicked = 'linkpop_amazon_affiliate_public_card_clicked/1.0',
  AmazonAffiliateBuyOnAmazonClicked = 'linkpop_amazon_affiliate_buy_on_amazon_clicked/1.0',
}

const getTrekkieData = () => {
  const trekkieData = window?.trekkie?.trekkie?.defaultAttributes;
  const {
    visitToken,
    microSessionId,
    microSessionCount,
    uniqToken,
  }: {
    visitToken: string;
    microSessionId: string;
    microSessionCount: string;
    uniqToken: string;
  } = trekkieData || {};

  return {
    visitToken,
    microSessionId,
    microSessionCount,
    uniqToken,
  };
};

type TrackEventProps =
  | {
      event: MonorailEvent.TrackShoppingBag;
      eventType?: ShoppingBagEvent;
      data: {
        currency?: string;
        products?: [ProductType];
        pageId: string;
        totalValue?: number;
        cartId?: string;
      };
    }
  | {
      event: MonorailEvent.ClickLink;
      data: {
        linkId: string;
        linkTitle: string;
        linkType: string;
        linkUrl?: string;
        productId?: string;
        pageId: string;
      } & ({linkUrl: string} | {productId: string});
    }
  | {
      event: MonorailEvent.AmazonAffiliatePublicCardClicked;
      data: {
        linkUrl: string;
        linkTitle: string;
        pageId: string;
      };
  }
  | {
      event: MonorailEvent.AmazonAffiliateBuyOnAmazonClicked;
      data: {
        linkUrl: string;
        linkTitle: string;
        pageId: string;
      };
  }
  | {
      event: MonorailEvent.ViewHome | MonorailEvent.ClickLandingPageCreatePage;
      data: {};
    };


function commonBrowserPayload() {
  const {uniqToken, visitToken, microSessionId, microSessionCount} =
    getTrekkieData();

  return {
    uniqToken,
    visitToken,
    microSessionId,
    microSessionCount: Number(microSessionCount),
    locale:
      navigator?.languages && navigator?.languages.length
        ? navigator.languages[0]
        : '',
    referrer: document?.referrer || '',
    search: window.location.search,
    pageEnglishUrl: window.location?.href || '',
    url: window.location?.href || '',
  };
}

export function trackEvent(props: TrackEventProps) {
  switch (props.event) {
    case MonorailEvent.TrackShoppingBag: {
      return trekkieLoaded.then(() => {
        const {pageId} = props.data;
        const eventPayload = getShoppingBagEventPayload(
          props.eventType,
          props.data,
        );
        const {visitToken} = getTrekkieData();
        const payload = {
          ...eventPayload,
          visitToken,
          pageId,
          eventType: eventPayload?.eventType,
        };

        // will need to use trekkie.track for checkout event since it redirects
        const formattedPayload = toUnderscoreCase(payload);
        window.trekkie.track(
          `monorail://${MonorailEvent.TrackShoppingBag}`,
          formattedPayload,
        );
      });
    }

    case MonorailEvent.ClickLink: {
      return trekkieLoaded.then(() => {
        const {linkId, linkUrl, linkTitle, linkType, productId, pageId} =
          props.data;
        const {visitToken, microSessionId} = getTrekkieData();
        // current schema requires underscore_case for payload keys since
        // monorail.produce formats it that way by default
        const payload = toUnderscoreCase({
          linkId,
          linkUrl,
          linkTitle,
          linkType,
          productId,
          pageId,
          visitToken,
          microSessionId,
        });

        window.trekkie.track(`monorail://${MonorailEvent.ClickLink}`, payload);
      });
    }

    case MonorailEvent.ViewHome: {
      return trekkieLoaded.then(() => {
        window.trekkie.track(`monorail://${MonorailEvent.ViewHome}`, {
          ...commonBrowserPayload(),
        });
      });
    }

    case MonorailEvent.ClickLandingPageCreatePage: {
      return trekkieLoaded.then(() => {
        const {visitToken, microSessionId} = getTrekkieData();

        return getMonorail().produce({
          schemaId: MonorailEvent.ClickLandingPageCreatePage,
          payload: {
            visitToken,
            microSessionId,
          },
        });
      });
    }

    case MonorailEvent.AmazonAffiliatePublicCardClicked: {
      return trekkieLoaded.then(() => {
        const {linkUrl, linkTitle} = props.data;

        return getMonorail().produce({
          schemaId: MonorailEvent.AmazonAffiliatePublicCardClicked,
          payload: {
            ...commonBrowserPayload(),
            linkUrl,
            linkTitle,
          },
        });
      });
    }

    case MonorailEvent.AmazonAffiliateBuyOnAmazonClicked: {
      return trekkieLoaded.then(() => {
        const {linkUrl, linkTitle} = props.data;
        return getMonorail().produce({
          schemaId: MonorailEvent.AmazonAffiliateBuyOnAmazonClicked,
          payload: {
            ...commonBrowserPayload(),
            linkUrl,
            linkTitle,
          },
        });
      });
    }
  }
}

export const timedTrackEvent = (eventData: TrackEventProps, time = 200) => {
  const trackPromise = trackEvent(eventData);

  // eslint-disable-next-line promise/param-names
  const escapePromise = new Promise((_, reject) => {
    setTimeout(() => {
      reject(new Error('tracking call exceeded specified time'));
    }, time);
  });

  return Promise.race([trackPromise, escapePromise]);
};

export const initiateAnalytics = async () => {
  initiateTrekkie();
};

// Sets up a promise that will be resolved later, once the trekkie script loads.
// The trekkie script tag's onload callback calls
// `globalThis.handleTrekkieLoad()`, thus resolving the promise.
// Awaiting trekkieLoaded to be resolves ensures that trekkie is available.
const trekkieLoaded = new Promise<void>((res) => {
  globalThis.handleTrekkieLoad = res;
});

const initiateTrekkie = async () => {
  const trekkieConfig = {
    Trekkie: {
      appName: 'linkpop',
    },
  };

  const trekkieVersion = '2020.10.20';
  // eslint-disable-next-line no-multi-assign
  const analytics = (window.analytics = window.analytics || []);

  if (analytics.integrations) {
    return;
  }

  analytics.methods = ['identify', 'page', 'ready', 'track'];
  analytics.factory = function (method: () => void) {
    return function () {
      // eslint-disable-next-line prefer-rest-params
      const args = Array.prototype.slice.call(arguments);
      args.unshift(method);
      analytics.push(args);
      return analytics;
    };
  };
  for (const key of analytics.methods) {
    analytics[key] = analytics.factory(key);
  }

  analytics.load = function (config: TrekkieConfig) {
    analytics.config = config;
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.onload = () => globalThis.handleTrekkieLoad();
    script.async = true;
    script.src = `https://cdn.shopify.com/s/javascripts/tricorder/trekkie.${config.Trekkie.appName}.min.js?v=${trekkieVersion}`;
    const first = document.getElementsByTagName('script')[0];
    first.parentNode?.insertBefore(script, first);
  };

  analytics.load(trekkieConfig);
};

export type useTrackPageViewProps = {
  pageId?: string;
};
export function useTrackPageView(payload?: useTrackPageViewProps) {
  const [payloadState, setPayloadState] = useState(payload);
  const {pageId} = payloadState || {};
  const didRecordPageView = useRef(false);
  useEffect(() => {
    const optionalPageIdCondition =
      typeof pageId === 'string' ? pageId !== '' : true;
    // wait for document.title after redirects and trekkie to be loaded
    setTimeout(() => {
      if (optionalPageIdCondition && didRecordPageView.current === false) {
        trekkieLoaded.then(() => {
          window.trekkie.page(`monorail://${MonorailEvent.PageView}`, {
            pageEnglishUrl: window.location.href,
            locale:
              navigator?.languages && navigator?.languages.length
                ? navigator.languages[0]
                : '',
            pageId,
          });
          didRecordPageView.current = true;
        });
      }
    }, 1000);
  }, [pageId]);
  return setPayloadState;
}
